import { useQuery } from '@tanstack/react-query'
import { analyticsFetch, getCallerAgentId, hasLiveAnalytics } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  toMemberPnlResult,
  type AnalyticsMemberPnlPage,
  type MemberPnlResult,
} from '@/api/map/pnl'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

const PAGE_SIZE = 100
const MAX_PAGES = 50

function rangeQuery(range: { from: string; to: string }): string {
  return new URLSearchParams({
    from: range.from.slice(0, 10),
    to: range.to.slice(0, 10),
  }).toString()
}

function emptyMemberPnl(): MemberPnlResult {
  return {
    rows: [],
    asOf: undefined,
    martStale: false,
    commissionAvailable: false,
  }
}

async function fetchAllMemberPnlPages(agentId: string, qs: string): Promise<AnalyticsMemberPnlPage[]> {
  const pages: AnalyticsMemberPnlPage[] = []
  for (let page = 0; page < MAX_PAGES; page++) {
    const live = await analyticsFetch<AnalyticsMemberPnlPage>(
      `/v1/agents/${agentId}/member-pnl?${qs}&page=${page}&size=${PAGE_SIZE}&sort=pnl`,
    )
    pages.push(live)
    const got = live.rows?.length ?? 0
    const total = live.total
    if (got < PAGE_SIZE) break
    if (typeof total === 'number' && (page + 1) * PAGE_SIZE >= total) break
  }
  return pages
}

async function loadMemberPnl(query: api.PnlQuery): Promise<MemberPnlResult> {
  if (hasLiveAnalytics()) {
    const id = query.rootId ?? getCallerAgentId()
    if (!id) return emptyMemberPnl()
    try {
      const pages = await fetchAllMemberPnlPages(id, rangeQuery(query.range))
      return toMemberPnlResult(pages)
    } catch (err) {
      if (isUnreachableApiError(err)) return emptyMemberPnl()
      throw err
    }
  }

  const rows = await api.getMemberPnl(query)
  return {
    rows,
    asOf: new Date().toISOString(),
    martStale: false,
    commissionAvailable: true,
  }
}

export function useMemberPnl(query: api.PnlQuery) {
  return useQuery({
    queryKey: keys.memberPnl(query),
    queryFn: () => loadMemberPnl(query),
    placeholderData: (previous) => previous,
  })
}
